import { useMemo, useRef } from 'react';

import { type TransactionPaymentsFilterInput, useGetTransactionPaymentsQuery } from 'data/__generated__';

import { DataTable, type DataTableFiltersApi } from 'components/DataTable';

import { buildPaymentsColumns, type PaymentRow } from '../constants/paymentsColumns';
import { paymentPath } from './PaymentIdLink';

// Les liens profonds (`?userIds=…` depuis une fiche utilisateur) visent des filtres déclarés : la `DataTable` les relit
// dans l'URL et les affiche en puces, la page n'a rien à lire elle-même.
export const PaymentsList = () => {
    const filtersApiRef = useRef<DataTableFiltersApi | null>(null);
    const columns = useMemo(
        () => buildPaymentsColumns({ onSelectId: (id) => filtersApiRef.current?.setFilter('searchValue', id) }),
        [],
    );

    // `filters: {}` et non absent : `filters` est dans les `keyArgs` de `transactionPayments`, et la `DataTable` le
    // renvoie toujours (objet vide sans filtre) au `fetchMore` — sans quoi la page 2 s'écrit sous une autre entrée
    // de cache que celle que la liste lit, et le défilement ne charge jamais rien.
    const { loading, data, error, refetch, fetchMore } = useGetTransactionPaymentsQuery({
        variables: { filters: {} },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    if (error) {
        throw new Error('Une erreur est survenue lors de la récupération des paiements');
    }

    return (
        <div className="flex h-full w-full flex-col overflow-hidden">
            <DataTable<PaymentRow, TransactionPaymentsFilterInput>
                columns={columns}
                data={data?.transactionPayments.transactionPayments ?? []}
                filterMode="server"
                getRowId={(row) => row._id}
                getRowLink={(row) => paymentPath(row._id)}
                hasMore={data?.transactionPayments.hasMore ?? false}
                isLoading={loading}
                refetch={refetch}
                fetchMore={fetchMore}
                searchPlaceholder="ID du moyen de paiement, du paiement ou de la transaction"
                searchFilterKey="searchValue"
                filtersApiRef={filtersApiRef}
            />
        </div>
    );
};
